import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Grid,
  Stack,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
  Divider,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { listAllActivities, resetAllActivities } from "../../../store/actions";
import { tokens } from "../../../theme";
import { post } from "../../../services";
import { ADD_EXTRA_INVOICE } from "../../../graphql";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useProSidebar } from "react-pro-sidebar";

const AddExtraBillModal = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { collapsed } = useProSidebar();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [customer, setCustomer] = useState({ name: "", email: "", mobile: "" });
  const [pmtStatus, setPmtStatus] = useState("Unpaid");
  // const [invoiceNo, setInvoiceNo] = useState("");

  const { activitiesList, loading } = useSelector(({ activities }) => ({
    activitiesList: activities?.activities?.list || [],
    loading: activities?.activities?.loading || false,
  }));

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: collapsed ? "80vw" : "65vw",
    maxHeight: "85vh",
    overflowY: "auto",
    bgcolor: colors.primary[400],
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
  };

  const handleOpen = () => {
    setOpen(true);
    dispatch(resetAllActivities());
    dispatch(listAllActivities());
  };

  const handleClose = () => {
    setOpen(false);
    setSelected({});
    setCustomer({ name: "", email: "", mobile: "" });
    setPmtStatus("Pending");
    // setInvoiceNo("");
  };

  // Increment / Decrement
  const handleIncrement = useCallback((id) => {
    setSelected((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  }, []);

  const handleDecrement = useCallback((id) => {
    setSelected((prev) => {
      const updated = { ...prev };
      if (updated[id] > 1) updated[id] -= 1;
      else delete updated[id];
      return updated;
    });
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    let subtotal = 0;
    activitiesList.forEach((a) => {
      if (selected[a._id]) subtotal += a.rate * selected[a._id];
    });
    const tax = subtotal * 0.18;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  }, [activitiesList, selected]);

  // Sort activities: selected first
  const sortedActivities = useMemo(() => {
    return [...activitiesList].sort((a, b) => {
      const countA = selected[a._id] || 0;
      const countB = selected[b._id] || 0;
      return countB - countA;
    });
  }, [activitiesList, selected]);

  const handleSave = async () => {
    if (!customer.name.trim()) {
      toast.error("Customer Name is required.");
      return;
    }

    const selectedList = activitiesList
      .filter((a) => selected[a._id])
      .map((a) => ({ _id: a._id, name: a.name, count: selected[a._id] }));

    if (!selectedList.length) {
      toast.error("Please select at least one activity.");
      return;
    }

    const invoiceData = {
      customer_details: customer,
      pmt_status: pmtStatus,
      // inv_no: invoiceNo,
      activities: selectedList,
    };

    try {
      const apiResponse = await post("/graphql", {
        query: ADD_EXTRA_INVOICE,
        variables: invoiceData,
      });

      const invoice = apiResponse?.data?.data?.add_extra_invoice;

      if (invoice?._id) {
        toast.success("Invoice created successfully!");
        handleClose();
        navigate(`/billing/extras/${invoice._id}`);
      } else {
        console.error("Failed to create invoice:", apiResponse.data);
        toast.error("Failed to create invoice. Check console for details.");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error("Error creating invoice. Check console for details.");
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" onClick={handleOpen}>
          Add Extras Bill
        </Button>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            Add Extra Activities Invoice
          </Typography>

          <CustomerForm
            customer={customer}
            setCustomer={setCustomer}
            pmtStatus={pmtStatus}
            setPmtStatus={setPmtStatus}
            // invoiceNo={invoiceNo}
            // setInvoiceNo={setInvoiceNo}
          />

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Stack alignItems="center" py={5}>
              <CircularProgress />
              <Typography mt={2}>Loading activities...</Typography>
            </Stack>
          ) : activitiesList.length > 0 ? (
            <>
              <Typography fontWeight={600} mb={1}>
                Available Activities
              </Typography>
              <Grid container spacing={2}>
                {sortedActivities.map((activity) => (
                  <ActivityCard
                    key={activity._id}
                    activity={activity}
                    count={selected[activity._id] || 0}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    theme={theme}
                  />
                ))}
              </Grid>
              <TotalsBox totals={totals} colors={colors} />
            </>
          ) : (
            <Typography color="text.secondary" py={4} textAlign="center">
              No activities available.
            </Typography>
          )}

          <Stack mt={3} direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSave}
              disabled={Object.keys(selected).length === 0 || !customer.name}
            >
              Save Invoice ({Object.keys(selected).length})
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default AddExtraBillModal;

const CustomerForm = ({
  customer,
  setCustomer,
  pmtStatus,
  setPmtStatus,
  // invoiceNo,
  // setInvoiceNo,
}) => (
  <Box mb={3}>
    <Typography fontWeight={600} mb={1}>
      Customer Details
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Customer Name"
          value={customer.name}
          onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Email"
          value={customer.email}
          onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Mobile"
          value={customer.mobile}
          onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Payment Status"
          select
          value={pmtStatus}
          onChange={(e) => setPmtStatus(e.target.value)}
        >
          <MenuItem value="Paid">Paid</MenuItem>
          <MenuItem value="Unpaid">Unpaid</MenuItem>
        </TextField>
      </Grid>
      {/* <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Invoice Number"
          value={invoiceNo}
          onChange={(e) => setInvoiceNo(e.target.value)}
        />
      </Grid> */}
    </Grid>
  </Box>
);

const ActivityCard = ({ activity, count, onIncrement, onDecrement, theme }) => (
  <Grid item xs={12} sm={6} md={4} key={activity._id}>
    <Stack
      p={2}
      borderRadius={1}
      bgcolor={
        count > 0
          ? theme.palette.primary.light
          : theme.palette.background.default
      }
      spacing={1}
      border={`1px solid ${theme.palette.divider}`}
    >
      <Typography fontWeight={600}>{activity.name}</Typography>
      {activity.description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={activity.description}
        >
          {activity.description}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        ₹{activity.rate}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
        mt={1}
      >
        <IconButton
          size="small"
          onClick={() => onDecrement(activity._id)}
          disabled={count === 0}
        >
          <Remove fontSize="small" />
        </IconButton>
        <Typography fontWeight={600} minWidth={40} textAlign="center">
          {count}
        </Typography>
        <IconButton size="small" onClick={() => onIncrement(activity._id)}>
          <Add fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  </Grid>
);

const TotalsBox = ({ totals, colors }) => (
  <Box mt={3} p={2} borderRadius={1} bgcolor={colors.primary[500]}>
    <Typography variant="subtitle1" fontWeight={600}>
      Subtotal: ₹{totals.subtotal.toFixed(2)}
    </Typography>
    <Typography variant="subtitle1" fontWeight={600}>
      Tax (18%): ₹{totals.tax.toFixed(2)}
    </Typography>
    <Typography variant="h6" fontWeight={700} color="secondary" mt={1}>
      Total: ₹{totals.total.toFixed(2)}
    </Typography>
  </Box>
);
